import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react'
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from '@tanstack/react-query'
import { apiGet, apiPatch } from '~/util/api'
import { BrowserRouter, useLocation } from 'react-router'
import { Toaster } from '~/lib/ui/toast'
import { TranslationProvider } from '../shared/TranslationProvider'
import translations from './locales.json'

const pathPrefix = '/coach/dashboard_candidates/'
const DETAIL_TYPE_PARAM = 'detail_type'
const DETAIL_ITEM_ID_PARAM = 'detail_item_id'

const CandidateContext = createContext()

export const useCandidate = () => useContext(CandidateContext)

export const queryClient = new QueryClient()

const CandidateProviderInner = ({
  coach_id,
  program_level,
  platformName,
  candidateFirstName,
  coachFirstName,
  coachCalendlyLink,
  candidateName,
  candidateEmail,
  industries,
  companySizes,
  coachHistories,
  locales,
  children,
}) => {
  const locale = locales.candidate
  const location = useLocation()
  const candidateId = location.pathname.split('/').pop()
  const urlParams = new URLSearchParams(location.search)
  const tabParam = urlParams.get('tab')
  const activeTabParam = urlParams.get('active_tab')
  const detailType = urlParams.get(DETAIL_TYPE_PARAM)
  const detailItemId = urlParams.get(DETAIL_ITEM_ID_PARAM)
  const defaultActiveTab = tabParam || activeTabParam || 'coach_services'
  const queryPath = [pathPrefix, candidateId, '.json'].join('')
  const queryFn = () => apiGet(queryPath).then((res) => res.data)
  const { error, data: candidateData } = useQuery({
    queryKey: ['candidate', candidateId],
    queryFn,
  })

  const coachCanInitiateServiceRequest =
    program_level === 'SELECT' || program_level === 'CONCIERGE'

  const handleTabChange = (tabValue) => {
    const updatedParams = new URLSearchParams(window.location.search)
    updatedParams.set('tab', tabValue)
    // Normalize on the new tab param to avoid stale precedence from active_tab.
    updatedParams.delete('active_tab')

    if (tabValue === 'coach_services' && detailType) {
      updatedParams.delete(DETAIL_TYPE_PARAM)
      updatedParams.delete(DETAIL_ITEM_ID_PARAM)
    }

    const queryString = updatedParams.toString()
    const nextUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname

    window.history.replaceState({}, '', nextUrl)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  if (error) {
    console.error('error retrieving candidate', error)
  }
  const { candidate } = candidateData || {}

  const [candidateStatus, setCandidateStatus] = useState(
    candidate?.candidateStatus,
  )

  const [candidateStates, setCandidateStates] = useState(
    candidate?.candidateStates || [],
  )

  const [showCandidateStatusDialog, setShowCandidateStatusDialog] =
    useState(false)

  const toggleShowCandidateStatusDialog = () =>
    setShowCandidateStatusDialog(!showCandidateStatusDialog)

  useEffect(() => {
    if (candidate?.candidateStatus) {
      setCandidateStatus(candidate.candidateStatus)
    }
    if (candidate?.candidateStates) {
      setCandidateStates(candidate.candidateStates)
    }
  }, [candidate])

  const [candidateState, setCandidateState] = useState(candidate)

  // Update candidateState when candidateData changes
  useEffect(() => {
    if (candidate) {
      setCandidateState(candidate)
    }
  }, [candidate])

  // Update setNewCandidateStatus to update candidateState as well
  const setNewCandidateStatus = async (v) => {
    const res = await apiPatch(
      `/coach/candidates/${candidate.id}/assign_candidate_state`,
      {
        candidate: {
          candidate_profile_attributes: { candidate_state: v['status'] },
        },
        pause: v['pause'] || null,
        _method: 'patch',
      },
    )

    const data = res.data
    if (res.status === 200) {
      setCandidateStatus(data.candidateStatus)
      setCandidateStates(data.candidateStates)
      setShowCandidateStatusDialog(false)
      return res
    } else {
      throw new Error('res not ok')
    }
  }

  const idRef = useRef(null)

  const value = {
    candidate: candidateState,
    setCandidate: setCandidateState, // <-- expose setter
    detailType,
    detailItemId,
    defaultActiveTab,
    candidateStatus,
    setCandidateStatus,
    candidateStates,
    setCandidateStates,
    showCandidateStatusDialog,
    setShowCandidateStatusDialog,
    toggleShowCandidateStatusDialog,
    setNewCandidateStatus,
    coachId: coach_id,
    candidateId,
    programLevel: program_level,
    handleTabChange,
    candidateFirstName,
    coachFirstName,
    coachCalendlyLink,
    candidateName,
    candidateEmail,
    coachCanInitiateServiceRequest,
    idRef,
    industries,
    companySizes,
    coachHistories,
    locale,
    platformName,
  }
  return (
    <CandidateContext.Provider value={value}>
      {children}
      <Toaster />
    </CandidateContext.Provider>
  )
}

export const CandidateProvider = ({ children, ...props }) => {
  const { locales } = props
  const locale = locales?.candidate

  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope={'javascript.components.CoachCandidateView.CandidateProvider'}
    >
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <CandidateProviderInner {...props}>{children}</CandidateProviderInner>
        </QueryClientProvider>
      </BrowserRouter>
    </TranslationProvider>
  )
}
export default CandidateProvider
